/**
 * TaskContextSummary.jsx — Iter 172 §1 (Josh-approved, Feb 2026)
 *
 * Renders the AI-generated context blurb under a task card. Surfaces:
 *   1. "From call 5/27 · Connected — Office Mgr (2m 14s)" header line
 *      (built from `ai_context_source_type` + `ai_context_outcome`).
 *   2. The 1–2 sentence excerpt itself (`ai_context_summary`).
 *   3. Collapse-to-2-lines with "Show more" affordance when the
 *      summary overflows.
 *
 * Empty-state behaviour matches the spec:
 *   - Manual task with a description -> show the description verbatim.
 *   - Manual task with no description -> show the empty-state nudge
 *     ("No context recorded — add a note to this task.").
 *
 * Read-only on the card. Editing happens in the task detail modal —
 * we never mutate from this component.
 */
import React, { useState, useRef, useLayoutEffect } from "react";
import { Sparkles } from "lucide-react";

function _formatSourceLine(task) {
  const kind = task?.ai_context_source_type;
  const outcome = task?.ai_context_outcome;
  const gen = task?.ai_context_generated_at;
  let label = "";
  if (kind === "call") label = "From call";
  else if (kind === "voicemail") label = "From voicemail";
  else if (kind === "email") label = "From email";
  else if (kind) label = `From ${kind}`;
  let date = "";
  if (gen) {
    try {
      const d = new Date(gen);
      date = d.toLocaleDateString(undefined, { month: "numeric", day: "numeric" });
    } catch { /* ignore */ }
  }
  const parts = [label, date && `· ${date}`, outcome && `· ${outcome}`].filter(Boolean);
  return parts.join(" ");
}

export default function TaskContextSummary({ task, compact = false }) {
  const [expanded, setExpanded] = useState(false);
  const [overflowing, setOverflowing] = useState(false);
  const textRef = useRef(null);

  const aiSummary = (task?.ai_context_summary || "").trim();
  const description = (task?.description || "").trim();

  useLayoutEffect(() => {
    if (!textRef.current || expanded) return;
    const el = textRef.current;
    setOverflowing(el.scrollHeight > el.clientHeight + 1);
  }, [aiSummary, description, expanded]);

  // Empty-state — no AI summary AND no manual description.
  if (!aiSummary && !description) {
    return (
      <div
        data-testid={`task-context-empty-${task?.id || ""}`}
        style={{
          marginTop: 4,
          fontSize: 11,
          color: "var(--text-tertiary)",
          fontStyle: "italic",
        }}
      >
        No context recorded — add a note to this task.
      </div>
    );
  }

  // Prefer AI summary; fall back to manual description for manually-created tasks.
  const isAI = !!aiSummary;
  const text = aiSummary || description;
  const sourceLine = isAI ? _formatSourceLine(task) : null;

  const baseFont = compact ? 11 : 12;

  return (
    <div
      data-testid={`task-context-${task?.id || ""}`}
      data-ai={isAI ? "true" : "false"}
      style={{
        marginTop: 6,
        paddingLeft: 8,
        borderLeft: isAI ? "2px solid #A78BFA" : "2px solid #E5E7EB",
      }}
    >
      {isAI && sourceLine && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 4,
            fontSize: 10,
            color: "#7C3AED",
            fontWeight: 600,
            marginBottom: 2,
          }}
        >
          <Sparkles style={{ width: 10, height: 10 }} />
          <span>{sourceLine}</span>
        </div>
      )}
      <div
        ref={textRef}
        style={{
          fontSize: baseFont,
          color: "var(--text-secondary, #4B5563)",
          lineHeight: 1.45,
          display: "-webkit-box",
          WebkitLineClamp: expanded ? "none" : 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}
      >
        {text}
      </div>
      {overflowing && !expanded && (
        <button
          onClick={(e) => { e.stopPropagation(); setExpanded(true); }}
          data-testid={`task-context-expand-${task?.id || ""}`}
          style={{
            marginTop: 2,
            background: "none",
            border: "none",
            padding: 0,
            cursor: "pointer",
            fontSize: 10,
            fontWeight: 600,
            color: "#3C3489",
          }}
        >
          Show more
        </button>
      )}
      {expanded && (
        <button
          onClick={(e) => { e.stopPropagation(); setExpanded(false); }}
          data-testid={`task-context-collapse-${task?.id || ""}`}
          style={{
            marginTop: 2,
            background: "none",
            border: "none",
            padding: 0,
            cursor: "pointer",
            fontSize: 10,
            fontWeight: 600,
            color: "#3C3489",
          }}
        >
          Show less
        </button>
      )}
    </div>
  );
}
